/**
 * EMBER & PALM — asymmetric editorial homepage; genuine dishes lead; parchment relieves charcoal;
 * Palm Ember Gold punctuates actions; each section advances discovery toward booking.
 */
import { Fragment } from "react";
import { Link } from "wouter";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock3,
  MapPin,
  Phone,
  Star,
  Users,
} from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { AmokLegendTeaser } from "@/components/AmokLegend";
import { HouseBeerPromo } from "@/components/HouseBeerPromo";
import { SITE, trackEvent, verifiedReviews } from "@/lib/site";

const signatures = [
  {
    number: "01",
    name: "Fish Amok",
    description:
      "A fragrant steamed coconut curry with aromatic kroeung, mackerel, Chinese broccoli and peanuts.",
    note: "A signature Cambodian dish",
    image: SITE.images.fishAmok,
    srcSet: `${SITE.images.fishAmok480} 480w, ${SITE.images.fishAmok640} 640w, ${SITE.images.fishAmok960} 960w, ${SITE.images.fishAmok} 1600w`,
    alt: "Traditional Fish Amok at ANGKOR Cambodian Flavours in West Melbourne",
  },
  {
    number: "02",
    name: "My Grandmother’s Dish",
    description:
      "Juicy marinated free-range chicken with mixed salad, mint and grandmother’s dipping sauce.",
    note: "A family recipe",
    price: "$29.60",
    image: SITE.images.grandmothersDish,
    srcSet: `${SITE.images.grandmothersDish480} 480w, ${SITE.images.grandmothersDish640} 640w, ${SITE.images.grandmothersDish960} 960w, ${SITE.images.grandmothersDish} 1600w`,
    alt: "My Grandmother’s Dish with grilled chicken, salad and dipping sauce at ANGKOR Cambodian Flavours",
  },
  {
    number: "03",
    name: "Beef Loc Lac",
    description:
      "Wok-seared marinated beef with mixed salad, onions and a fried egg — a generous Cambodian favourite.",
    note: "A Cambodian favourite",
    image: SITE.images.beefLocLac,
    srcSet: `${SITE.images.beefLocLac480} 480w, ${SITE.images.beefLocLac640} 640w, ${SITE.images.beefLocLac960} 960w, ${SITE.images.beefLocLac} 1600w`,
    alt: "Beef Loc Lac with fried egg and fresh vegetables at ANGKOR Cambodian Flavours",
  },
];

export default function Home() {
  return (
    <>
      <PageMeta
        title="ANGKOR Cambodian Flavours | Cambodian Restaurant West Melbourne"
        description="Authentic Cambodian restaurant at 389 Spencer St, West Melbourne, near Southern Cross and Marvel Stadium. Fish Amok, Khmer family recipes. Book a table."
        path="/"
        image={SITE.images.fishAmok}
      />

      <section className="hero-section">
        <picture className="hero-texture" aria-hidden="true">
          <source media="(max-width: 767px)" srcSet={SITE.images.heroTextureMobile} />
          <source media="(max-width: 1023px)" srcSet={SITE.images.heroTextureSmall} />
          <img src={SITE.images.heroTexture} alt="" width="1600" height="900" fetchPriority="high" loading="eager" />
        </picture>
        <div className="hero-wash" aria-hidden="true" />
        <div className="container hero-grid">
          <div className="hero-copy reveal">
            <p className="eyebrow">389 Spencer Street · West Melbourne</p>
            <h1>Authentic Cambodian Flavours. <em>Family Recipes.</em> Melbourne.</h1>
            <p className="hero-lede">
              Discover traditional Fish Amok, fragrant kroeung, charcoal-grilled favourites and family recipes inspired by Chef Neri’s Cambodian heritage.
            </p>
            <a
              href={SITE.booking}
              className="hero-marvel-chip"
              onClick={() =>
                trackEvent("book_table_click", {
                  placement: "homepage_hero",
                })
              }
            >
              <span>Book a Table</span> <ArrowRight aria-hidden="true" />
            </a>
            <div className="hero-actions">
              <a
                href={SITE.booking}
                className="button button-gold"
                onClick={() => trackEvent("book_table_click", { placement: "hero" })}
              >
                Book a Table <CalendarDays size={17} />
              </a>
              <Link href="/menu" className="button button-ghost" onClick={() => trackEvent("menu_view", { placement: "hero" })}>
                View Menu
              </Link>
              <a href={SITE.directOrder} className="text-link light" onClick={() => trackEvent("order_online_click", { placement: "hero" })}>
                Order Online &amp; Gift Cards <ArrowRight size={16} />
              </a>
            </div>
          </div>

          <div className="hero-visual reveal reveal-late">
            <div className="hero-image-frame">
              <img
                src={SITE.images.fishAmok}
                srcSet={`${SITE.images.fishAmok480} 480w, ${SITE.images.fishAmok640} 640w, ${SITE.images.fishAmok960} 960w, ${SITE.images.fishAmok} 1600w`}
                sizes="(max-width: 767px) 84vw, (max-width: 1023px) 74vw, 44vw"
                alt="Traditional Fish Amok at ANGKOR Cambodian Flavours in West Melbourne"
                width="1800"
                height="1200"
                fetchPriority="high"
              />
            </div>
            <Link
              href="/products"
              className="hero-fish-float"
              aria-label="Shop ANGKOR Amok Curry Paste"
              onClick={() => trackEvent("shop_click", { placement: "floating_fish_amok" })}
            >
              <img src={SITE.images.fishAmokFloat} alt="" width="240" height="240" loading="eager" />
              <span aria-hidden="true">Shop</span>
            </Link>
            <div className="hero-caption">
              <span>Signature</span>
              <strong>Fish Amok</strong>
            </div>
          </div>
        </div>
      </section>

      <HouseBeerPromo showBooking />

      <section className="proof-bar" aria-label="Restaurant highlights">
        <div className="container proof-grid">
          <a href={SITE.reviews} target="_blank" rel="noreferrer" onClick={() => trackEvent("review_link_click", { placement: "proof_bar" })}>
            <span className="stars" aria-label={`${SITE.rating} out of 5 stars`}><Star fill="currentColor" /> <Star fill="currentColor" /> <Star fill="currentColor" /> <Star fill="currentColor" /> <Star fill="currentColor" /></span>
            <strong>{SITE.rating} on Google</strong>
            <small>{SITE.reviewCount} reviews · checked {SITE.reviewChecked} · Read reviews</small>
          </a>
          <div><MapPin /><strong>West Melbourne</strong><small>Near Southern Cross & Marvel Stadium</small></div>
          <div><Clock3 /><strong>Lunch & Dinner</strong><small>Open Tuesday to Sunday</small></div>
          <div><Users /><strong>Events & Functions</strong><small>Event feasts from $79 per adult</small></div>
        </div>
      </section>

      <section className="section parchment-section" id="signatures">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow dark">01 — Signatures of Angkor</p>
              <h2>Dishes with a story <em>at the table.</em></h2>
            </div>
            <p>Fragrant, fresh and made from scratch. If Cambodian food is new to you, begin here.</p>
          </div>

          <div className="signature-editorial">
            {signatures.map((dish, index) => (
              <Fragment key={dish.name}>
                <article className={`signature-story signature-story-${index + 1}`}>
                  <div className="signature-image-wrap">
                    <img
                      src={dish.image}
                      srcSet={dish.srcSet}
                      sizes="(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) calc(100vw - 64px), min(42vw, 560px)"
                      alt={dish.alt}
                      width="1200"
                      height="900"
                      loading="lazy"
                    />
                    <span className="image-folio">{dish.number}</span>
                  </div>
                  <div className="signature-copy">
                    <p className="dish-note">{dish.note}</p>
                    <h3>{dish.name}</h3>
                    <p>{dish.description}</p>
                    {dish.price && <p><strong className="text-[var(--gold-ink)]">{dish.price}</strong></p>}
                    <Link href="/menu" className="text-link">Explore the menu <ArrowRight size={16} /></Link>
                  </div>
                </article>
                {index === 0 && (
                  <AmokLegendTeaser
                    headingLevel="h3"
                    placement="home_signature"
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="section story-section">
        <div className="container story-grid">
          <div className="story-image-stack">
            <img src={SITE.images.interior} alt="Dining room at ANGKOR Cambodian Flavours, 389 Spencer Street" width="1800" height="1200" loading="lazy" />
            <img src={SITE.images.korKoh} alt="Slow-cooked Beef Stew Kor Koh at ANGKOR Cambodian Flavours" width="1200" height="900" loading="lazy" />
          </div>
          <div className="story-copy">
            <p className="eyebrow">02 — Chef Neri’s Story</p>
            <h2>A dish that started <em>with family.</em></h2>
            <p className="lead">
              At ANGKOR, food is a way of remembering and welcoming. Chef Neri draws on memories of family cooking in Cambodia — the perfume of lemongrass, herbs crushed for kroeung and generous dishes passed around the table.
            </p>
            <p>
              My Grandmother’s Dish carries that feeling into the restaurant: familiar, personal and made to be shared. It is one part of a larger story — introducing Melbourne to the depth, freshness and warmth of Khmer cooking.
            </p>
            <Link href="/our-story" className="button button-outline">Discover Neri’s Story <ChevronRight size={17} /></Link>
          </div>
        </div>
      </section>

      <section className="section cuisine-section">
        <div className="container cuisine-grid">
          <div className="cuisine-copy">
            <p className="eyebrow dark">03 — Discover Cambodian Cuisine</p>
            <h2>Bright herbs. Slow comfort. <em>A cuisine to share.</em></h2>
            <p>Cambodian food balances fragrant aromatics, fresh herbs, gentle heat and savoury depth. You do not need to know the language of the menu — our team will guide you.</p>
          </div>
          <dl className="ingredient-list">
            <div><dt>Kroeung</dt><dd>A fresh Cambodian aromatic paste, often built from lemongrass, galangal, turmeric and makrut lime.</dd></div>
            <div><dt>Fish Amok</dt><dd>A softly steamed coconut fish curry and one of Cambodia’s most recognised traditional dishes.</dd></div>
            <div><dt>Khmer curries</dt><dd>Warm, aromatic curries with coconut, vegetables and herbs — comforting rather than simply hot.</dd></div>
            <div><dt>Table culture</dt><dd>Starters, salads, mains, rice and roti are at their best when shared around the table.</dd></div>
          </dl>
        </div>
      </section>

      <section className="section menu-preview">
        <div className="container menu-preview-grid">
          <div className="menu-visual">
            <img src={SITE.images.betelLeaf} alt="Prawn betel leaf starter at ANGKOR Cambodian Flavours" width="1200" height="900" loading="lazy" />
            <img src={SITE.images.seafood} alt="Cambodian seafood and rice dish at ANGKOR Cambodian Flavours" width="1200" height="900" loading="lazy" />
          </div>
          <div className="menu-copy">
            <p className="eyebrow">04 — At the Table</p>
            <h2>Begin with signatures. <em>Stay for the feast.</em></h2>
            <div className="menu-mini-list">
              <div><span>Fish Amok</span><small>Coconut · kroeung · mackerel</small><strong>$34.50</strong></div>
              <div><span>Beef Loc Lac</span><small>Wok-seared beef · salad · fried egg</small><strong>$32.90</strong></div>
              <div><span>Royal Khmer Restaurant Feast</span><small>Regular dining · minimum 2 guests</small><strong>$99 pp</strong></div>
              <div><span>Black Rice Pudding</span><small>Coconut cream · sesame</small><strong>$15.90</strong></div>
            </div>
            <Link href="/menu" className="button button-gold">View Full Menu <ArrowRight size={17} /></Link>
            <Link href="/lunch" className="text-link light">See weekday lunch <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="section reviews-section">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow dark">05 — Loved by Melbourne Diners</p>
              <h2>Warm words from <em>our guests.</em></h2>
            </div>
            <div className="rating-lockup"><strong>{SITE.rating}</strong><span><b>★★★★★</b><small>{SITE.reviewCount} Google reviews · checked {SITE.reviewChecked}</small></span></div>
          </div>
          <div className="review-grid">
            {verifiedReviews.map((review) => (
              <blockquote key={review.name}>
                <span aria-hidden="true">“</span>
                <p>{review.quote}</p>
                <footer>{review.name} · Google Review</footer>
              </blockquote>
            ))}
          </div>
          <a className="text-link" href={SITE.reviews} target="_blank" rel="noreferrer" onClick={() => trackEvent("review_link_click", { placement: "reviews_section" })}>
            Read more Google reviews <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <section className="section experience-editorial">
        <div className="container experience-editorial-grid">
          <div className="experience-photos">
            <img src={SITE.images.atmosphere} alt="Warm evening atmosphere at ANGKOR Cambodian Flavours" width="1800" height="1200" loading="lazy" />
            <img src={SITE.images.angkorTropical} alt="Angkor Tropical cocktail at ANGKOR Cambodian Flavours Restaurant & Bar" width="1600" height="2000" loading="lazy" />
          </div>
          <div className="experience-copy">
            <p className="eyebrow">06 — The Angkor Evening</p>
            <h2>Cambodian flavours. <em>Melbourne energy.</em></h2>
            <p>Begin with an Angkor Tropical or another Cambodian-inspired drink, share a table of signatures and let the evening unfold in a warm room made for conversation.</p>
            <a href={SITE.booking} className="button button-gold" onClick={() => trackEvent("book_table_click", { placement: "experience" })}>Book Your Evening</a>
          </div>
        </div>
      </section>

      <section className="section groups-section">
        <div className="container groups-grid">
          <div>
            <p className="eyebrow dark">07 — Groups & Events</p>
            <h2>Gather around <em>the table.</em></h2>
            <p>Weddings, birthdays, engagements and corporate functions feel at home with Chef Neri’s Cambodian feasts, attentive banquet service and an intimate West Melbourne setting.</p>
            <div className="inline-actions">
              <Link href="/groups-functions" className="button button-dark">Plan Your Celebration</Link>
              <Link href="/groups-functions" className="text-link">View event packages <ArrowRight size={16} /></Link>
            </div>
          </div>
          <div className="banquet-summary">
            <p>Event feasts</p>
            <strong>From $79 <small>per adult</small></strong>
            <span>Minimum 12 adults</span>
            <hr />
            <p>Exclusive venue hire</p>
            <strong>Up to 38 <small>seated</small></strong>
            <span>No room-hire fee; minimum spend applies</span>
          </div>
        </div>
      </section>

      <section className="section location-teaser">
        <div className="container location-grid">
          <div>
            <p className="eyebrow">08 — Find Your Table</p>
            <h2>Cambodian dining in the heart of <em>West Melbourne.</em></h2>
            <p>{SITE.address}. Convenient to Southern Cross Station, Melbourne CBD, Docklands and Marvel Stadium.</p>
            <div className="contact-lines">
              <a href={`tel:${SITE.phone}`} onClick={() => trackEvent("phone_click", { placement: "location" })}><Phone /> {SITE.phoneDisplay}</a>
              <a href={SITE.directions} target="_blank" rel="noreferrer" onClick={() => trackEvent("directions_click", { placement: "location" })}><MapPin /> Get Directions</a>
            </div>
            <Link href="/location" className="button button-outline-light">Hours, map & transport</Link>
          </div>
          <img src={SITE.images.interior} alt="ANGKOR Cambodian Flavours restaurant interior in West Melbourne" width="1800" height="1200" loading="lazy" />
        </div>
      </section>

      <section className="final-cta">
        <div className="container">
          <p className="eyebrow dark">Your table is waiting</p>
          <h2>Come hungry. <em>We’ll guide you.</em></h2>
          <p>Tuesday to Sunday at 389 Spencer Street, West Melbourne.</p>
          <a href={SITE.booking} className="button button-gold" onClick={() => trackEvent("book_table_click", { placement: "final_cta" })}>Book a Table <CalendarDays size={17} /></a>
        </div>
      </section>
    </>
  );
}
